/**
 * Browser-side device profile collector. Privacy-reduced per spec §11:
 *   - NO font enumeration
 *   - NO behavioral biometrics (typing cadence, pointer entropy)
 *   - Only privacy-acceptable signals: UA, language, timezone, screen, WebGL,
 *     canvas-hash (SHA-256 of a tiny canvas render, never the image)
 *
 * Server never trusts the client-provided hash — it normalizes + HMACs again.
 */

import { baseUrl } from './api.js';

export interface DeviceProfile {
  userAgent: string;
  language: string;
  languages: string[];
  platform: string;
  timezone: string;
  screen: {
    width: number;
    height: number;
    colorDepth: number;
    pixelRatio: number;
  };
  browser: { cookiesEnabled: boolean; doNotTrack: string | null };
  webgl: { vendor: string | null; renderer: string | null } | null;
  canvasHash: string | null;
}

export interface RiskSessionResponse {
  session_id: string;
  decision: 'ALLOW' | 'CHALLENGE' | 'STEP_UP_3DS' | 'BLOCK';
  score: number;
  reasons: string[];
  token: string;
  expires_at: string;
  signals?: Record<string, unknown>;
  narration?: { headline: string; paragraphs: string[]; severity: string };
  /**
   * Present when the duplicate-payment guard fired. The SPA renders a
   * confirmation prompt before letting the shopper proceed.
   */
  possible_duplicate?: {
    payment_intent_id: string;
    matched_on_key: 'email' | 'phone';
    delta_seconds: number;
  };
}

export async function collectDeviceProfile(): Promise<DeviceProfile> {
  return {
    userAgent: navigator.userAgent,
    language: navigator.language,
    languages: Array.from(navigator.languages ?? []),
    platform: navigator.platform,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    screen: {
      width: window.screen.width,
      height: window.screen.height,
      colorDepth: window.screen.colorDepth,
      pixelRatio: window.devicePixelRatio,
    },
    browser: {
      cookiesEnabled: navigator.cookieEnabled,
      doNotTrack: navigator.doNotTrack ?? null,
    },
    webgl: getWebGLInfo(),
    canvasHash: await getCanvasHash(),
  };
}

function getWebGLInfo(): { vendor: string | null; renderer: string | null } | null {
  try {
    const canvas = document.createElement('canvas');
    const gl =
      (canvas.getContext('webgl') as WebGLRenderingContext | null) ??
      (canvas.getContext('experimental-webgl') as WebGLRenderingContext | null);
    if (!gl) return null;
    const dbg = gl.getExtension('WEBGL_debug_renderer_info') as {
      UNMASKED_VENDOR_WEBGL: number;
      UNMASKED_RENDERER_WEBGL: number;
    } | null;
    if (!dbg) return { vendor: null, renderer: null };
    return {
      vendor: (gl.getParameter(dbg.UNMASKED_VENDOR_WEBGL) as string) ?? null,
      renderer: (gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) as string) ?? null,
    };
  } catch {
    return null;
  }
}

async function getCanvasHash(): Promise<string | null> {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 240;
    canvas.height = 60;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.textBaseline = 'top';
    ctx.font = '16px Arial';
    ctx.fillStyle = '#f60';
    ctx.fillRect(10, 10, 100, 30);
    ctx.fillStyle = '#069';
    ctx.fillText('UPG Risk Probe', 12, 14);
    const dataUrl = canvas.toDataURL();
    const encoder = new TextEncoder();
    const data = encoder.encode(dataUrl);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    return Array.from(new Uint8Array(hashBuffer))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  } catch {
    return null;
  }
}

/**
 * Open a risk session against the API. Fail-open for collection — if the
 * device profile can't be built, we still call the API with empty signals
 * (the score engine flags MISSING_DEVICE_SIGNALS). Fail-closed for verification
 * — if the API doesn't return a token, the payment button stays disabled.
 */
export async function openRiskSession(input: {
  /**
   * Effective tenant id from the loaded checkout config. Sent as
   * `X-Tenant-Id` because `/v1/risk/session` is a pre-session public route:
   * there's no risk token yet to carry the tenant, and the opaque `/c/<token>`
   * path keeps the tenant out of the URL. Without this header `tenantOf(req)`
   * falls through to the API's default tenant — which doesn't exist in cloud —
   * and the call 404s ('Tenant not found'), starving the flow of a risk/replay
   * token. See bd y5v.
   */
  tenant?: string;
  amount?: { value: string; currency: string };
  shopper?: { email?: string; name?: string };
  productHint?: string;
}): Promise<RiskSessionResponse> {
  let deviceProfile: DeviceProfile | undefined;
  try {
    deviceProfile = await collectDeviceProfile();
  } catch {
    deviceProfile = undefined;
  }
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (input.tenant) headers['X-Tenant-Id'] = input.tenant;
  const res = await fetch(`${baseUrl}/v1/risk/session`, {
    method: 'POST',
    headers,
    credentials: 'include',
    body: JSON.stringify({
      amount: input.amount,
      shopper: input.shopper,
      product_hint: input.productHint,
      referrer: document.referrer,
      checkout_url: window.location.href,
      device_profile: deviceProfile,
    }),
  });
  if (!res.ok) {
    const j = (await res.json().catch(() => ({}))) as { error?: { message?: string } };
    throw new Error(j.error?.message ?? `risk session failed (${res.status})`);
  }
  return (await res.json()) as RiskSessionResponse;
}
